/**
 * "I am 21+ and agree to the Terms and Privacy Policy": unchecked by default,
 * used by the Register screen and TermsConsentGate. The links open the legal
 * pages in a new tab on web, so a half-filled form isn't lost.
 *
 * termsAcceptanceRecord() is what gets stored on users/{uid}.termsAcceptance.
 */
import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

export { CONSENT_REQUIRED_ERROR, termsAcceptanceRecord, hasCurrentConsent } from '../constants/legalVersion';

// On web the link is a real <a target="_blank">, so the browser opens the tab;
// calling window.open here as well would open it twice.
function openLegal(path, navigateTo, screen) {
  if (Platform.OS === 'web') return;
  navigateTo?.(screen);
}

export default function ConsentCheckbox({ checked, onChange, disabled = false, navigateTo, invalid = false, nativeID = 'terms-consent' }) {
  const link = (label, path, screen) => (
    <Text
      style={styles.link}
      onPress={() => openLegal(path, navigateTo, screen)}
      accessibilityRole="link"
      {...(Platform.OS === 'web' ? { href: path, hrefAttrs: { target: '_blank', rel: 'noopener' } } : {})}
    >
      {label}
    </Text>
  );
  return (
    <View style={styles.row}>
      <Pressable
        onPress={() => !disabled && onChange(!checked)}
        disabled={disabled}
        hitSlop={8}
        accessibilityRole="checkbox"
        accessibilityState={{ checked, disabled }}
        accessibilityLabel="I am 21 or older and agree to the Terms of Service and Privacy Policy"
        aria-checked={checked}
        aria-invalid={invalid || undefined}
        testID="terms-consent-checkbox"
        nativeID={nativeID}
        style={({ hovered, focused }) => [styles.box, checked && styles.boxChecked, invalid && !checked && styles.boxInvalid, (hovered || focused) && !disabled && styles.boxHover]}
      >
        {checked ? <Ionicons name="checkmark" size={14} color="#fff" /> : null}
      </Pressable>
      <Text style={styles.label}>
        I am 21 or older and agree to the {link('Terms of Service', '/terms', 'Terms')} and {link('Privacy Policy', '/privacy', 'Privacy')}.
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginTop: 4 },
  box: {
    width: 20, height: 20, borderRadius: 5, marginTop: 1,
    borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.45)', backgroundColor: 'transparent',
    alignItems: 'center', justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'background-color 150ms ease, border-color 150ms ease' } : {}),
  },
  boxHover: { borderColor: 'rgba(255,255,255,0.8)' },
  boxChecked: { backgroundColor: '#3B82F6', borderColor: '#3B82F6' },
  boxInvalid: { borderColor: '#F87171' },
  label: { flex: 1, color: 'rgba(255,255,255,0.72)', fontSize: 13, lineHeight: 19 },
  link: { color: '#93C5FD', textDecorationLine: 'underline', ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}) },
});
